import { Button, SelectOptionType } from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';
import Loading from '../../../../../components/info/Loading';

import {
  AxiosResult,
  AxiosResultDefaultError,
} from '../../../../../api/request';
import {
  Discount,
  DiscountFields,
  DiscountFormOptions,
  DiscountWithRelations,
  getDiscountById,
  getDiscountFormOptions,
  PeriodWithDates,
  saveDiscountRequest,
} from '../../api/discount';

import DiscountDataSection from './sections/DiscountDataSection';
import DiscountInicioSection from './sections/DiscountInicioSection';
import DiscountContinuidadSection from './sections/DiscountContinuidadSection';

const prefix = 'tariffAndTuition.discounts';

interface DiscountFormProps {
  recordToEdit: Discount | null;
  onSubmit: (result: AxiosResult<Discount, AxiosResultDefaultError>) => void;
  onCancel: () => void;
}

interface FormValues
  extends Pick<DiscountFields, 'code' | 'name' | 'description' | 'percentage'> {
  hasAutomaticAssignment: boolean;
  applyToAllNewAdmissionProcesses: boolean;
  minimumGradeAverage: string;
  inicioAcademicLevelIds: SelectOptionType[];
  inicioPeriodIds: SelectOptionType[];
  inicioStudyPlanIds: SelectOptionType[];
  inicioCampusIds: SelectOptionType[];
  inicioScheduleIds: SelectOptionType[];
  inicioModalityIds: SelectOptionType[];
  continuidadAcademicLevelIds: SelectOptionType[];
  continuidadStudyPlanIds: SelectOptionType[];
  continuidadCampusIds: SelectOptionType[];
  continuidadScheduleIds: SelectOptionType[];
  continuidadModalityIds: SelectOptionType[];
  continuidadPeriodIds: SelectOptionType[];
  enrollmentDates: { periodId: number; startDate: string; endDate: string }[];
}

const DEFAULT_VALUES: FormValues = {
  code: '',
  name: '',
  description: '',
  percentage: '',
  hasAutomaticAssignment: false,
  applyToAllNewAdmissionProcesses: false,
  minimumGradeAverage: '',
  inicioAcademicLevelIds: [],
  inicioPeriodIds: [],
  inicioStudyPlanIds: [],
  inicioCampusIds: [],
  inicioScheduleIds: [],
  inicioModalityIds: [],
  continuidadAcademicLevelIds: [],
  continuidadStudyPlanIds: [],
  continuidadCampusIds: [],
  continuidadScheduleIds: [],
  continuidadModalityIds: [],
  continuidadPeriodIds: [],
  enrollmentDates: [],
};

export const DiscountForm = ({
  recordToEdit,
  onSubmit: onSubmitProp,
  onCancel,
}: DiscountFormProps) => {
  const { t } = useTranslation();
  const editing = !!recordToEdit;

  const [formOptions, setFormOptions] = useState<DiscountFormOptions | null>(
    null,
  );
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [
    discountData,
    setDiscountData,
  ] = useState<DiscountWithRelations | null>(null);
  const isResettingRef = useRef(false);

  const methods = useForm<FormValues>({
    mode: 'onSubmit',
    defaultValues: DEFAULT_VALUES,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    control,
    setError,
    reset,
    setValue,
  } = methods;

  const continuidadPeriodIds = useWatch({
    control,
    name: 'continuidadPeriodIds',
  });

  // Load form options on mount
  useEffect(() => {
    const fetchOptions = async () => {
      setLoadingOptions(true);
      const result = await getDiscountFormOptions();
      if (result.data) {
        setFormOptions(result.data);
      }
      setLoadingOptions(false);
    };
    fetchOptions();
  }, []);

  // Load discount data when editing
  useEffect(() => {
    const fetchDiscountData = async () => {
      if (recordToEdit?.id) {
        const result = await getDiscountById(recordToEdit.id);
        if (result.data) {
          setDiscountData(result.data);
        }
      }
    };
    if (editing) {
      fetchDiscountData();
    }
  }, [recordToEdit?.id, editing]);

  // Update form when discount data and options are loaded
  useEffect(() => {
    if (!formOptions) return;

    if (discountData && editing) {
      const mapToOptions = (
        ids: number[] | undefined,
        options: { id: number; name: string }[],
      ): SelectOptionType[] => {
        if (!ids) return [];
        return ids
          .map((id) => {
            const opt = options.find((o) => o.id === id);
            return opt
              ? ({ value: id, label: opt.name } as SelectOptionType)
              : null;
          })
          .filter((o): o is SelectOptionType => o !== null);
      };

      const inicioAcademicLevelIds = mapToOptions(
        discountData.inicioAcademicLevels?.map((r) => r.academicLevelId),
        formOptions.academicLevels,
      );
      const inicioPeriodIds = mapToOptions(
        discountData.inicioPeriods?.map((r) => r.periodId),
        formOptions.periods,
      );
      const inicioStudyPlanIds = mapToOptions(
        discountData.inicioStudyPlans?.map((r) => r.studyPlanId),
        formOptions.studyPlans,
      );
      const inicioCampusIds = mapToOptions(
        discountData.inicioCampuses?.map((r) => r.campusId),
        formOptions.campuses,
      );
      const inicioScheduleIds = mapToOptions(
        discountData.inicioSchedules?.map((r) => r.scheduleId),
        formOptions.schedules,
      );
      const inicioModalityIds = mapToOptions(
        discountData.inicioModalities?.map((r) => r.modalityId),
        formOptions.modalities,
      );

      const continuidadAcademicLevelIds = mapToOptions(
        discountData.continuidadAcademicLevels?.map((r) => r.academicLevelId),
        formOptions.academicLevels,
      );
      const continuidadStudyPlanIds = mapToOptions(
        discountData.continuidadStudyPlans?.map((r) => r.studyPlanId),
        formOptions.studyPlans,
      );
      const continuidadCampusIds = mapToOptions(
        discountData.continuidadCampuses?.map((r) => r.campusId),
        formOptions.campuses,
      );
      const continuidadScheduleIds = mapToOptions(
        discountData.continuidadSchedules?.map((r) => r.scheduleId),
        formOptions.schedules,
      );
      const continuidadModalityIds = mapToOptions(
        discountData.continuidadModalities?.map((r) => r.modalityId),
        formOptions.modalities,
      );
      const continuidadPeriodIds = mapToOptions(
        discountData.continuidadPeriods?.map((r) => r.periodId),
        formOptions.periods,
      );

      const enrollmentDates =
        discountData.continuidadPeriods?.map((p) => ({
          periodId: p.periodId,
          startDate: p.enrollmentStartDate || '',
          endDate: p.enrollmentEndDate || '',
        })) || [];

      isResettingRef.current = true;
      reset({
        code: discountData.code,
        name: discountData.name,
        description: discountData.description ?? '',
        percentage: String(Math.round(Number(discountData.percentage))),
        hasAutomaticAssignment: discountData.hasAutomaticAssignment ?? false,
        applyToAllNewAdmissionProcesses:
          discountData.applyToAllNewAdmissionProcesses ?? false,
        minimumGradeAverage: discountData.minimumGradeAverage
          ? String(discountData.minimumGradeAverage)
          : '',
        inicioAcademicLevelIds,
        inicioPeriodIds,
        inicioStudyPlanIds,
        inicioCampusIds,
        inicioScheduleIds,
        inicioModalityIds,
        continuidadAcademicLevelIds,
        continuidadStudyPlanIds,
        continuidadCampusIds,
        continuidadScheduleIds,
        continuidadModalityIds,
        continuidadPeriodIds,
        enrollmentDates,
      });
    } else if (!editing) {
      isResettingRef.current = true;
      reset(DEFAULT_VALUES);
    }
  }, [formOptions, discountData, editing, reset]);

  // Update enrollment dates when continuidadPeriodIds changes (user interaction only)
  useEffect(() => {
    if (!continuidadPeriodIds || !formOptions) return;

    // Skip when form is being reset with server data
    if (isResettingRef.current) {
      isResettingRef.current = false;
      return;
    }

    const currentDates = methods.getValues('enrollmentDates') || [];
    const selectedPeriodIds = continuidadPeriodIds.map(
      (p: SelectOptionType) => p.value as number,
    );

    const newDates = selectedPeriodIds.map((periodId: number) => {
      const existing = currentDates.find((d) => d.periodId === periodId);
      return existing || { periodId, startDate: '', endDate: '' };
    });

    setValue('enrollmentDates', newDates);
  }, [continuidadPeriodIds, formOptions, setValue, methods]);

  const selectOptions = useMemo(() => {
    if (!formOptions) return null;

    return {
      academicLevels: formOptions.academicLevels.map((al) => ({
        value: al.id,
        label: al.name,
      })),
      periods: formOptions.periods.map((p) => ({
        value: p.id,
        label: p.name,
      })),
      studyPlans: formOptions.studyPlans.map((sp) => ({
        value: sp.id,
        label: sp.name,
      })),
      campuses: formOptions.campuses.map((c) => ({
        value: c.id,
        label: c.name,
      })),
      schedules: formOptions.schedules.map((s) => ({
        value: s.id,
        label: s.name,
      })),
      modalities: formOptions.modalities.map((m) => ({
        value: m.id,
        label: m.name,
      })),
    };
  }, [formOptions]);

  const onSubmit = useCallback(
    async (values: FormValues) => {
      const extractIds = (options: SelectOptionType[]): number[] =>
        options?.map((o) => o.value as number) || [];

      const continuidadPeriods: PeriodWithDates[] =
        values.continuidadPeriodIds?.map((period: SelectOptionType) => {
          const periodId = period.value as number;
          const enrollmentDate = values.enrollmentDates?.find(
            (ed) => ed.periodId === periodId,
          );
          return {
            periodId,
            enrollmentStartDate: enrollmentDate?.startDate || null,
            enrollmentEndDate: enrollmentDate?.endDate || null,
          };
        }) || [];

      const requestBody: Discount = {
        code: values.code,
        name: values.name,
        description: values.description,
        percentage: Number(values.percentage),
        hasAutomaticAssignment: values.hasAutomaticAssignment,
        applyToAllNewAdmissionProcesses: values.applyToAllNewAdmissionProcesses,
        minimumGradeAverage: values.minimumGradeAverage
          ? Number(values.minimumGradeAverage)
          : null,
        id: recordToEdit?.id,
        inicioAcademicLevelIds: extractIds(values.inicioAcademicLevelIds),
        inicioPeriodIds: extractIds(values.inicioPeriodIds),
        inicioStudyPlanIds: extractIds(values.inicioStudyPlanIds),
        inicioCampusIds: extractIds(values.inicioCampusIds),
        inicioScheduleIds: extractIds(values.inicioScheduleIds),
        inicioModalityIds: extractIds(values.inicioModalityIds),
        continuidadAcademicLevelIds: extractIds(
          values.continuidadAcademicLevelIds,
        ),
        continuidadStudyPlanIds: extractIds(values.continuidadStudyPlanIds),
        continuidadCampusIds: extractIds(values.continuidadCampusIds),
        continuidadScheduleIds: extractIds(values.continuidadScheduleIds),
        continuidadModalityIds: extractIds(values.continuidadModalityIds),
        continuidadPeriods,
      };

      const result = await saveDiscountRequest(requestBody);
      if (result.error && result.error.code === 'HTTP_ERROR') {
        let messageError = t('common.errors.codeError', { code: values.code });
        if (
          result.error.data.message[0] ===
          'code must contain only letters and numbers'
        ) {
          messageError = t('common.errors.onlyLettersNumbers');
        }
        setError('code', {
          message: messageError,
        });
        return;
      }
      onSubmitProp(result);
    },
    [onSubmitProp, setError, t, recordToEdit],
  );

  const getPeriodName = (periodId: number): string => {
    const period = formOptions?.periods.find((p) => p.id === periodId);
    return period?.name || '';
  };

  if (loadingOptions) {
    return (
      <div className="p-4 text-center">
        <Loading insideCard />
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <Form onSubmit={handleSubmit(onSubmit)} className="p-4">
        {/* Header with back button */}
        <Row className="mb-4">
          <Col>
            <Button
              type="button"
              text={t(`${prefix}.backToList`)}
              outlined
              icon="back"
              onClick={onCancel}
              size="sm"
              className="rounded-pill"
            />
          </Col>
        </Row>

        {/* Description text */}
        <Row className="mb-4">
          <Col xs="12">
            <p className="text-muted mb-0">{t(`${prefix}.formDescription`)}</p>
          </Col>
        </Row>

        <DiscountDataSection editing={editing} />

        {selectOptions && (
          <>
            <DiscountInicioSection selectOptions={selectOptions} />
            <DiscountContinuidadSection
              selectOptions={selectOptions}
              getPeriodName={getPeriodName}
            />
          </>
        )}

        {/* Action buttons */}
        <Row className="mt-5">
          <Col xs="12" className="d-flex justify-content-end">
            <Button
              type="button"
              text={t('common.actions.cancel')}
              outlined
              onClick={onCancel}
              className="mr-3"
            />
            <Button
              type="submit"
              text={t('common.actions.saveChanges')}
              loading={isSubmitting}
            />
          </Col>
        </Row>
      </Form>
    </FormProvider>
  );
};
